import Head from 'next/head';
import PropTypes from 'prop-types';
import ArticleTagList from '@/components/blog/ArticleTagList';
import CustomHeading from '@/components/blog/CustomHeading';
import MillesimaLink from '@/components/common/MillesimaLink';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { ARTICLE_CARD_TYPE, MINIMUM_IMG_SIZE, MINIMUM_IMG_SIZE_MEDIUM, POSITION_TYPE } from '@/constants/blog';
import { PAGES } from '@/constants/pages';
import { useAppSelector } from '@/redux';
import styles from './ArticleCard.module.scss';

export const ArticleCard = ({
	id,
	imageSrc,
	imageAlt,
	tags,
	title,
	shortDesc,
	publishedAt,
	urlKeyword,
	customization,
	isTopArticle = false,
	isLazyLoaded = false,
	articleCardType = ARTICLE_CARD_TYPE.SMALL,
}) => {
	const { locale } = useAppSelector(({ root }) => root.storeConf);
	const { isMobile } = useScreenSizeContext();
	const { headingLevel } = customization;
	const contentPositionClassName = `position-${!isTopArticle ? 'start' : 'center'}`;

	const ImageLink = (
		<MillesimaLink
			id="article-card-image"
			href={`${PAGES.BLOG}/${urlKeyword}`}
			target="_blank"
			ariaLabel="ArticleImageLink"
			customClassName={{ container: styles['card-img-top-container'] }}
			isNextLink={true}
		>
			<img
				className={styles['card-img-top']}
				src={imageSrc}
				alt={imageAlt}
				width={
					articleCardType === ARTICLE_CARD_TYPE.SMALL || isMobile ? MINIMUM_IMG_SIZE.WIDTH : MINIMUM_IMG_SIZE_MEDIUM.WIDTH
				}
				height={
					articleCardType === ARTICLE_CARD_TYPE.SMALL || isMobile
						? MINIMUM_IMG_SIZE.HEIGHT
						: MINIMUM_IMG_SIZE_MEDIUM.HEIGHT
				}
				loading={isLazyLoaded ? 'lazy' : null}
			/>
		</MillesimaLink>
	);

	const TitleLink = (
		<MillesimaLink
			id={`article-card-title-${id}`}
			href={`${PAGES.BLOG}/${urlKeyword}`}
			target="_blank"
			ariaLabel="ArticleLink"
			customClassName={{ container: styles['title-container'], link: styles['title-link'] }}
			isNextLink={true}
		>
			<CustomHeading
				title={title}
				headingLevel={headingLevel}
				labelPosition={POSITION_TYPE.START}
				customClassName={{ label: styles['title'] }}
			/>
		</MillesimaLink>
	);

	const ShortDesc = <p className={styles['short-desc']}>{shortDesc}</p>;

	const PublishedAt = <span className={styles['publication-date']}>{new Date(publishedAt).toLocaleDateString(locale)}</span>;

	return (
		<>
			{!isLazyLoaded && (
				<Head>
					<link rel="preload" href={imageSrc} as="image" />
				</Head>
			)}
			<div
				className={`${styles['container']} ${styles[contentPositionClassName]}`}
				data-test-id="article-card-container"
				data-rbf="article-card-container"
			>
				{ImageLink}
				<div className={styles['card-body']}>
					<ArticleTagList tags={tags} centered={isTopArticle} />
					{TitleLink}
					{ShortDesc}
					{PublishedAt}
				</div>
			</div>
		</>
	);
};

ArticleCard.propTypes = {
	id: PropTypes.string.isRequired,
	imageSrc: PropTypes.string.isRequired,
	imageAlt: PropTypes.string.isRequired,
	tags: PropTypes.array.isRequired,
	title: PropTypes.string.isRequired,
	shortDesc: PropTypes.string.isRequired,
	publishedAt: PropTypes.string.isRequired,
	urlKeyword: PropTypes.string.isRequired,
	customization: PropTypes.shape({
		contentPosition: PropTypes.oneOf([POSITION_TYPE.CENTER, POSITION_TYPE.START]),
		headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5]),
	}),
	isTopArticle: PropTypes.bool,
	isLazyLoaded: PropTypes.bool,
};

export default ArticleCard;
